/**
 * Renders API Service
 *
 * Client for fetching render data from the render-api service.
 * Used by the UserFolderPage to display rendered images.
 */

import configProvider from "../providers/configprovider";

/**
 * Render status types
 */
export type RenderStatus = "pending" | "processing" | "completed" | "failed";

/**
 * Render type
 */
export type RenderType = "image" | "360";

/**
 * Render object returned by the API
 */
export interface Render {
  id: string;
  name: string;
  type: RenderType;
  status: RenderStatus;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  createdAt: string;
  completedAt: string | null;
}

/**
 * Paginated response from the render API
 */
interface PaginatedRendersResponse {
  items: Render[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Get the base URL for the render API
 * Uses RENDER_API_URL from env-config.js
 */
function getBaseUrl(): string {
  return configProvider("RENDER_API_URL", "").replace(/\/$/, "");
}

/**
 * Fetch renders for a user folder
 *
 * @param folderId - The internal user folder UUID
 * @param page - Page number (default: 1)
 * @param pageSize - Number of items per page (default: 30)
 * @returns Promise<PaginatedRendersResponse>
 */
export async function fetchRenders(
  folderId: string,
  page: number = 1,
  pageSize: number = 30,
): Promise<PaginatedRendersResponse> {
  const baseUrl = getBaseUrl();

  if (!baseUrl) {
    console.error("REACT_APP_RENDER_API_URL environment variable is not set");
    throw new Error("Render API URL is not configured");
  }

  const url = `${baseUrl}/viewer/renders/${encodeURIComponent(folderId)}?page=${page}&pageSize=${pageSize}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { items: [], total: 0, page, pageSize };
      }
      throw new Error(
        `Failed to fetch renders: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data as PaginatedRendersResponse;
  } catch (error) {
    console.error("Error fetching renders:", error);
    throw error;
  }
}

/**
 * Check if any renders are still processing
 *
 * @param renders - Array of render objects
 * @returns boolean - True if any render is pending or processing
 */
export function hasProcessingRenders(renders: Render[]): boolean {
  return renders.some(
    (render) => render.status === "pending" || render.status === "processing",
  );
}

/**
 * Get completed renders only
 *
 * @param renders - Array of render objects
 * @returns Render[] - Array of completed renders with valid image URLs
 */
export function getCompletedRenders(renders: Render[]): Render[] {
  return renders.filter(
    (render) => render.status === "completed" && render.imageUrl,
  );
}
